import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  useMediaQuery,
  Button,
  CircularProgress,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import DirectionsIcon from "@mui/icons-material/Directions";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import EditLocationIcon from "@mui/icons-material/EditLocation";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AltRouteIcon from '@mui/icons-material/AltRoute';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import logo from "../../assets/images/logo.png";
import { Tooltip } from "@mui/material";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import ViewModuleOutlinedIcon from "@mui/icons-material/ViewModuleOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";

/* 🔒 Menú estático */
const menuConfig = [
  {
    section: "Inicio",
    items: [
      { title: "Inicio", path: "/inicio", icon: <DashboardOutlinedIcon /> },
    ],
  },
  {
    section: "Servicios",
    items: [
      { title: "Dashboard", path: "/dashboard", icon: <BarChartOutlinedIcon /> },
    ],
  },
  {
    section: "Administración",
    items: [
      { title: "Usuarios", path: "/usuarios", icon: <GroupOutlinedIcon /> },
      { title: "Licencias", path: "/licencias", icon: <ViewModuleOutlinedIcon /> },
      { title: "Permisos", path: "/permisos", icon: <SecurityOutlinedIcon /> },
      { title: "Reportes", path: "/reportes", icon: <AssessmentIcon /> },
      { title: "Empresas", path: "/empresas", icon: <BusinessOutlinedIcon /> },
      { title: "Vehiculos", path: "/vehiculos", icon: <DirectionsCarOutlinedIcon /> },
    ],
  },
  {
    section: "Configuración",
    items: [
      { title: "Configuración", path: "/configuracion", icon: <SettingsOutlinedIcon /> },
    ],
  },
];

const Sidebar = () => {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [modulosPermitidos, setModulosPermitidos] = useState([]);
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:768px)");

  const toggleSidebar = () => setOpen(!open);

  /* 🔒 Cargar módulos permitidos */
  useEffect(() => {
    const fetchModulos = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        logout();
        return;
      }

      try {
        const res = await axios.get(
          process.env.REACT_APP_OBTENERMODULOS,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (res.data.success) {
          setModulosPermitidos(
            res.data.modulos.map((m) => m.ruta.toLowerCase())
          );
        } else {
          logout();
        }
      } catch (error) {
        console.error("Error cargando módulos:", error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    fetchModulos();
  }, []);

  /* 🔐 Validar ruta */
  const isAllowed = (path) =>
    modulosPermitidos.includes(path.toLowerCase());

  /* 🎨 Estilos */
  const getButtonStyle = (path) => {
    const active = location.pathname.toLowerCase() === path.toLowerCase();

    return {
      borderRadius: "10px",
      mb: "4px",
      px: 1.5,
      py: 0.7,
      minHeight: "38px",

      display: "flex",
      alignItems: "center",
      gap: "8px",

      bgcolor: active ? "#2FA4B8" : "transparent",
      color: active ? "white" : "#334155",

      transition: "all 0.15s ease",

      "&:hover": {
        bgcolor: active ? "#2FA4B8" : "#F1F5F9",
        color: active ? "white" : "#0F172A",
      },
    };
  };

  /* 🚪 Logout */
  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userData");
    navigate("/");
  };

  const drawerWidth = collapsed ? 72 : 240;

  return (
    <>
      {/* 🔝 Navbar */}
      <AppBar position="fixed" sx={{ backgroundColor: "#0F2F44", zIndex: 1100 }}>
        <Toolbar>
          <IconButton color="inherit" onClick={() => setCollapsed(!collapsed)}>
            <MenuIcon />
          </IconButton>

          <Typography
            sx={{
              flexGrow: 1,
              textAlign: "center",
              fontFamily: "Orbitron, sans-serif",
              fontWeight: 600,
              letterSpacing: "6px",
              fontSize: "26px",
              color: "#9AA3AD",
              textTransform: "uppercase"
            }}
          >
            CRONOS
          </Typography>

          <Button color="inherit" startIcon={<ExitToAppIcon />} onClick={logout}>
            Cerrar Sesión
          </Button>
        </Toolbar>
      </AppBar>

      {/* 📌 Sidebar */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? open : true}
        onClose={toggleSidebar}
        sx={{
          width: drawerWidth,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            marginTop: "64px",
            height: "calc(100vh - 64px)",
            overflowY: "auto",
            overflowX: "hidden",

            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },

            backgroundColor: "#FFFFFF",
            borderRight: "1px solid #E5E7EB",
            transition: "all 0.2s ease"
          },
        }}
      >
        <Box textAlign="center" my={1.5}>
          <img
            src={logo}
            alt="Logo"
            style={{
              width: collapsed ? "40px" : "110px",
              transition: "0.2s",
              marginTop: "2px",
              marginBottom: "2px"
            }}
          />
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper elevation={0} sx={{ px: 1.2, py: 1 }}>
            {menuConfig.map((section) => {
              const visibleItems = section.items.filter((item) =>
                isAllowed(item.path)
              );

              if (!visibleItems.length) return null;

              return (
                <Box key={section.section} mb={2}>
                  {!collapsed && (
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "11px",
                        textTransform: "uppercase",
                        color: "#94A3B8",
                        letterSpacing: "0.8px",
                        px: 1,
                        mb: 0.8
                      }}
                    >
                      {section.section}
                    </Typography>
                  )}

                  <List sx={{ p: 0 }}>
                    {visibleItems.map((item) => (
                      <ListItem key={item.path} disablePadding>
                        <Tooltip title={collapsed ? item.title : ""} placement="right">
                          <ListItemButton
                            component={Link}
                            to={item.path}
                            sx={getButtonStyle(item.path)}
                            onClick={isMobile ? toggleSidebar : undefined}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                mr: collapsed ? 0 : 1.5,
                                justifyContent: "center",
                                color:
                                  location.pathname.toLowerCase() === item.path.toLowerCase()
                                    ? "white"
                                    : "#64748B",
                                transition: "0.2s",
                                "&:hover .MuiListItemIcon-root": {
                                  color: "#0F172A"
                                }
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            {!collapsed && <ListItemText primary={item.title} />}
                          </ListItemButton>
                        </Tooltip>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              );
            })}
          </Paper>
        )}
      </Drawer>
    </>
  );
};

export default Sidebar;
